import { Injectable } from '@angular/core';
import { User } from './user';
import { StorageService } from '../../core/storage/storageService';
import { NavService } from '../ui/navService';
import { GatewayAPIService } from '../../core/gateway-api.service';
import { SignInResponse } from './signInResponse';
import { Router } from '@angular/router';
import { Page } from '../../components/ui/filterable-table/tableViewAbstractComponent';
import { ExternalProcessorService } from '../externalProcessor/externalProcessorService';
import { Logging } from '../../core/logging';
import { Observable, Subject } from 'rxjs';
import { UserRole } from '../../../../../../libs/Shared/components/model/UserRoles';


@Injectable({
    providedIn: 'root'
})
export class UserService {

    isImpersonating$: Observable<boolean|undefined>;
    private isImpersonatingSubject: Subject<boolean|undefined>;
    //impersonatedUser$: Observable<User|undefined>;
    //private impersonatedUserSubject: BehaviorSubject<User|undefined>;

    constructor(
        private api: GatewayAPIService,
        private navService: NavService, 
        private storage: StorageService,
        private router: Router,
        private externalProcessorService: ExternalProcessorService,
    ) {
        this.isImpersonatingSubject = new Subject<boolean|undefined>();
        this.isImpersonating$ = this.isImpersonatingSubject.asObservable();
        //this.impersonatedUserSubject = new BehaviorSubject<User|undefined>(storage.getImpersonateUser());
        //this.impersonatedUser$ = this.impersonatedUserSubject.asObservable();

        const query: Record<string, string>|undefined  = window.location.href
            .split('?')[1]
            ?.split('&')
            .reduce((acc: Record<string, string>, el: string) => {
                const kv = el.split('=');
                acc[kv[0]] = kv[1];
                return acc;
            },{});

        // TODO (jacob): add key
        //console.log(`userid = ${query?.['userid']}`);

    }

    // TODO (jacob): have caller handle all errors
    public async signin(username: string, password: string) : Promise<SignInResponse> {

        return this.api.apiCallSane(
            '/auth/sign-in',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            { username, password },
            { authenticate: false, updateAuthHeader: true });
    }

    public async authenticate2FA(userId:string, code:string): Promise<SignInResponse> {
        return this.api.apiCallSane(
            '/auth/tfa',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            { userId: userId, code: code },
            { authenticate: true, updateAuthHeader: true });
    }

    public async forcePasswordReset(userId:string): Promise<void> {
        return this._patch({
            userId: userId,
            requirePasswordReset: true
        });
    }

    public async forgotPassword(email:string): Promise<boolean> {
        return this.api.apiCallSane(
            '/auth/forgot-password',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            {email: email},
            { authenticate: true, setRoot: true }
        );
    }

    public async resetPassword(oldPassword:string, password:string, resetCode:string|null, email:string|null): Promise<SignInResponse> {

        return this.api.apiCallSane(
            '/auth/reset-password',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            { oldPassword, password, resetCode, email },
            { authenticate: true, updateAuthHeader: true });

    }

    public async initializeSession(sessionInfo: SignInResponse, isImpersonating: boolean = false) {

        if (!sessionInfo.user) {
            throw "Error in session. Please log in again."
        }

        console.log("initializeSession", sessionInfo);

        let user:User = new User(sessionInfo.user);

        this.storage.setCurrentUser(user);
        this.storage.setRoleAccessMap(this.navService.generateAccessTable(StorageService.getAllAccessNamespaces(), user.accessBits));

        if (sessionInfo.navMenu) {
            this.storage.setCurrentNav(sessionInfo.navMenu);
        }

        if (sessionInfo.allAccessNamespaces) {
            this.storage.setAllAccessNamespaces(sessionInfo.allAccessNamespaces);
        }

        // load the user's account

        // TODO: change to account
        if (sessionInfo.account) {
            this.storage.setMyAccount(sessionInfo.account);
            this.storage.setSelectedAccount(sessionInfo.account);
            if (sessionInfo.locations) {
                //this.storage.setMyMerchants(sessionInfo.locations.records);
                this.storage.setNumberOfMyBusinesses(sessionInfo.locations.length);
            }
            else {
                this.storage.setNumberOfMyBusinesses(0);
            }
        }
        else {
            this.storage.setNumberOfMyBusinesses(0)
        }

        if (sessionInfo.locations) {
            if (sessionInfo.locations.length == 1) {
                this.storage.setSelectedLocation(sessionInfo.location!);
            }
        }
        try {
            await this.externalProcessorService.sync();
        } catch (error) {
            Logging.verbose("user-service", "initializeSession", "Error with sync", error, user);
        }

    }

    public async impersonateUser(userToImpersonateId: string) {

        let usr = this.storage.getCurrentUser();

        if (!usr) {
            throw "unable to impersonate user";
        }

        let impersonatingUserId = usr.id;

        let response:SignInResponse = await this.getUserToImpersonate(userToImpersonateId, impersonatingUserId);

        if (response == undefined) {
            throw "an error occurred while attempting to impersonate user";
        }
        else if (response.user == undefined) {
            throw "unable to impersonate user";
        }

        this.storage.setImpersonatingSession(this.router.url);

        this.initializeSession(response, true);

        this.isImpersonatingSubject.next(true);


        if (this.storage.getSelectedLocation()) {
            this.router.navigateByUrl("merchants");
        }
        else {
            this.router.navigateByUrl("dashboard");
        }
    }

    public async endUserImpersonation(): Promise<any> {

        return this.api.apiCallSane(
            '/auth/impersonate-end',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            { },
            { authenticate: true, updateAuthHeader: true })
        .then(response => {
            this.storage.restoreUserSession();
            this.isImpersonatingSubject.next(false);
        })

    }

    public getAccountUsers(accountId:string): Promise<Page<User>> {
        return this.getPaged(accountId, "", "", 1, 30);
    }

    public getUserByEmail(emailAddress:string): Promise<User> {
        let user = User.createEmpty();
        user.emailAddress = emailAddress;
        return this._get({emailAddress:emailAddress});
    }

    public getSuiteUserByEmail(email:string): Promise<User> {
        return this.api.apiCallSane(
            '/suite/user',
            'GET',
            this.api.getDefaultHeaders(),
            {email},
            null,
            { authenticate: true, setRoot: true }
        )

    }

    public getUserById(id:string): Promise<User> {
        let user = User.createEmpty();
        user.id = id;
        return this._get({id:id});
    }


    public revertUserRole(userId:string, role: UserRole): Promise<void> {
        return this.putRole(userId, role);
    }

    public disableAccount(userId:string): Promise<void> {
        return this._patch(
            {
                userId: userId,
                isDisabled: true
            }
        )
    }

    public enableAccount(userId:string): Promise<void> {
        return this._patch(
            {
                userId: userId,
                isDisabled: false
            }
        )
    }

    public saveUserInfo(user: Partial<User>): Promise<void> {
        return this._put(user);
    }

    public saveUserAccessBits(userId: string, grantNamespaces:string[], revokeNamespaces:string[]): Promise<void> {
        return this._patch({
            userId: userId,
            grant:grantNamespaces,
            revoke:revokeNamespaces            
        })
    }

    public copyUserAccess(fromUserId:string, toUserId:string): Promise<boolean> {
        return this.api.apiCallSane(
            '/admin/user/copy-access',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            { sourceUserId: fromUserId, destUserId: toUserId },
            { authenticate: true, setRoot: true }
        )
    }

    public createUser(user:Partial<User>, role: string): Promise<void> {
        return this._post(user, role);
    }


    public getUserGroups(): Promise<string[]> {

        return this.api.apiCallSane(
            '/admin/usergroups',
            'GET',
            this.api.getDefaultHeaders(),
            {},
            null,
            { authenticate: true, setRoot: true }
        )
    }

    public getUserGroupUsers(groupName:string): Promise<Partial<User>[]> {

        return this.api.apiCallSane(
            '/admin/usergroup/users',
            'GET',
            this.api.getDefaultHeaders(),
            {groupName:groupName},
            null,
            { authenticate: true, setRoot: true }
        )
    }

    public addUserGroup(groupName:string): Promise<void> {

        return this.api.apiCallSane(
            '/admin/usergroup',
            'PUT',
            this.api.getDefaultHeaders(),
            null,
            {groupName:groupName},
            { authenticate: true, setRoot: true });
    }

    public removeUserGroup(groupName:string): Promise<void> {

        return this.api.apiCallSane(
            '/admin/usergroup',
            'DELETE',
            this.api.getDefaultHeaders(),
            null,
            {groupName:groupName},
            { authenticate: true, setRoot: true });
    }

    public addUserToGroup(userId:string, groupName:string): Promise<void> {

        return this.api.apiCallSane(
            '/admin/usergroup/user',
            'PUT',
            this.api.getDefaultHeaders(),
            null,
            {userId:userId, groupName:groupName},
            { authenticate: true, setRoot: true });
    }

    public removeUserFromGroup(userId:string, groupName:string): Promise<void> {

        return this.api.apiCallSane(
            '/admin/usergroup/user',
            'DELETE',
            this.api.getDefaultHeaders(),
            null,
            {userId:userId, groupName:groupName},
            { authenticate: true, setRoot: true });
    }


    private _post(user: Partial<User>, role: string): Promise<void> {

        let data = { user:user, role: role };

        return this.api.apiCallSane(
            '/admin/user',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            data,
            { authenticate: true, setRoot: true });
    }

    private _put(data:any): Promise<void> {

        return this.api.apiCallSane(
            '/admin/user',
            'PUT',
            this.api.getDefaultHeaders(),
            null,
            data,
            { authenticate: true, setRoot: true });
    }

    putRole(userId: string, role: UserRole): Promise<void> {

        return this.api.apiCallSane(
            '/admin/user/role',
            'PUT',
            this.api.getDefaultHeaders(),
            null,
            {
                userId:userId,
                role:role
            },
            { authenticate: true, setRoot: true });
    }

    private _patch(data:any): Promise<void> {
        return this.api.apiCallSane(
            '/admin/user',
            'PATCH',
            this.api.getDefaultHeaders(),
            null,
            data,
            { authenticate: true, setRoot: true });
    }

    private _get(data: any): Promise<User> {
        return this.api.apiCallSane(
            '/user',
            'GET',
            this.api.getDefaultHeaders(),
            data,
            null,
            { authenticate: true, setRoot: true })
        //.then((us: Partial<User>[]) => us.map(u => new User(u)));
    }

    getPaged(accountId:string|undefined, merchantId:string|undefined, query:string|undefined, pageNum:number = 0, pageSize:number = 20, orderBy:string = "last_name", sortColumn = "", sortDirection = "asc"): Promise<Page<User>> {

        return this.api.apiCallSane(
            '/users',
            'GET',
            this.api.getDefaultHeaders(),
            {
                businessId: accountId ? accountId : "",
                merchantId: merchantId ? merchantId : "",
                query: query,
                pageNum: pageNum,
                pageSize: pageSize,
                orderBy: orderBy,
                sortColumn:sortColumn,
                sortDirection: sortDirection
            },
            null,
            { authenticate: true, setRoot: true }
        ).then((response: Partial<Page<User>>) => new Page<User>(response))
    }

    getPagedAdmin(query:string|undefined, pageNum:number = 0, pageSize:number = 20, orderBy:string = "last_name", sortColumn = "", sortDirection = "asc"): Promise<Page<User>> {

        return this.api.apiCallSane(
            '/admin/users',
            'GET',
            this.api.getDefaultHeaders(),
            {
                q: query,
                pageNum: pageNum,
                pageSize: pageSize,
                orderBy: orderBy,
                sortColumn:sortColumn,
                sortDirection: sortDirection
            },
            null,
            { authenticate: true, setRoot: true }
        ).then((response: Partial<Page<User>>) => new Page<User>(response))
    }


    // TODO: add role info params
    public addAccountUser(accountId: string, user:Partial<User>): Promise<void> {

        return this.api.apiCallSane(
            '/business/user',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            {
                businessId: accountId,
                userId: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                emailAddress: user.emailAddress
            },
            { authenticate: true, setRoot: true }
        );
    }

    public addMerchantUser(merchantId:string, user:Partial<User>, role: UserRole): Promise<void> {
        const data = {
            merchantId: merchantId,
            contact: user,
            role: role
        }
        return this.api.apiCallSane(
            '/merchant/user',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            data,
            { authenticate: true, setRoot: true }
        );
    }

    public addUserToMerchant(merchantId:string, user:Partial<User>, role: UserRole): Promise<void> {
        return this.api.apiCallSane(
            '/merchant/user',
            'PUT',
            this.api.getDefaultHeaders(),
            null,
            {
                merchantId: merchantId,
                user: user,
                role: role
            },
            { authenticate: true, setRoot: true }
        );
    }

    public signout(): Promise<void> {
        return this.api.apiCallSane(
            '/auth/sign-out',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            null,
            { authenticate: true, setRoot: true }
        ).then(() => {
            this.storage.clearSession();
            this.storage.removeAuthHeader();
        });
    }

    private async getUserToImpersonate(userToImpersonateId:string, impersonatingUserId:string) : Promise<SignInResponse> {

        return this.api.apiCallSane(
            '/auth/impersonate',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            { userToImpersonateId, impersonatingUserId },
            { updateAuthHeader: true })
        .then(data => {
            return data;
        });
    }

}
